import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  ReceiptLong as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { fillBirForm } from '../../../../utils/birFormFiller';
import { calculateBirTax } from '../../../../utils/taxCalculations';
import TemplateMapper from './TemplateMapper';

const BIRForms = () => {
  // Template Mapping State
  const [isMapperOpen, setIsMapperOpen] = useState(false);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // Object: { "Payee Name": [CAFOA_Objects] }
  const [selectedDocument, setSelectedDocument] = useState(null); // For detail view
  
  // Similar Suppliers State
  const [similarSuppliers, setSimilarSuppliers] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false); // Track if search was attempted

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isSelectSupplierOpen, setIsSelectSupplierOpen] = useState(false);
  const [manualSupplierSearch, setManualSupplierSearch] = useState('');
  const [manualSupplierResults, setManualSupplierResults] = useState([]);
  const [manualSupplierLoading, setManualSupplierLoading] = useState(false);

  // Add Supplier State
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [newSupplierData, setNewSupplierData] = useState({
    name: '',
    tin: '',
    address: '',
    zip_code: '',
    atc_2306: '',
    nature_of_payment_2306: '',
    atc_2307: '',
    nature_of_payment_2307: '',
    tax_rate_2306: '0',
    tax_rate_2307: '0'
  });

  // Preview State
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success' | 'error' | 'warning' | 'info'
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  useEffect(() => {
    if (selectedDocument && selectedDocument.name) {
      fetchSimilarSuppliers(selectedDocument.name);
    } else {
        setSimilarSuppliers([]);
        setSearchPerformed(false);
    }
    setSelectedSupplier(null);
  }, [selectedDocument]);

  const activeSupplier = useMemo(() => {
    if (selectedSupplier) return selectedSupplier;
    if (!similarSuppliers || similarSuppliers.length === 0) return null;
    const targetName = selectedDocument?.name ? selectedDocument.name.toLowerCase() : '';
    const exactMatch = targetName
      ? similarSuppliers.find(s => s.name && s.name.toLowerCase() === targetName)
      : null;
    return exactMatch || similarSuppliers[0];
  }, [selectedSupplier, similarSuppliers, selectedDocument]);

  const fetchSimilarSuppliers = async (payeeName) => {
    setSupplierLoading(true);
    setSearchPerformed(false);
    try {
        // Simple fuzzy logic: Split name by space, take words > 3 chars
        const words = payeeName.split(' ').filter(w => w.length > 3).map(w => w.replace(/[^a-zA-Z0-9]/g, ''));
        
        if (words.length === 0) {
             setSimilarSuppliers([]);
             setSupplierLoading(false);
             setSearchPerformed(true);
             return;
        }

        // Construct OR filter: name.ilike.%word1%,name.ilike.%word2%
        const filter = words.map(w => `name.ilike.%${w}%`).join(',');

        const { data, error } = await supabase
            .from('suppliers')
            .select('*')
            .or(filter)
            .limit(5);

        if (error) throw error;
        setSimilarSuppliers(data || []);

    } catch (error) {
        console.error('Error fetching similar suppliers:', error);
    } finally {
        setSupplierLoading(false);
        setSearchPerformed(true);
    }
  };

  const handleOpenAddSupplier = () => {
    setIsEditMode(false);
    setEditingSupplierId(null);
    setNewSupplierData({
        name: selectedDocument?.name || '',
        tin: '',
        address: '',
        zip_code: '',
        atc_2306: '',
        nature_of_payment_2306: '',
        atc_2307: '',
        nature_of_payment_2307: '',
        tax_rate_2306: '0',
        tax_rate_2307: '0'
    });
    setIsAddSupplierOpen(true);
  };

  const handleOpenEditSupplier = (supplier) => {
    setIsEditMode(true);
    setEditingSupplierId(supplier.id);
    setNewSupplierData({
        name: supplier.name || '',
        tin: supplier.tin || '',
        address: supplier.address || '',
        zip_code: supplier.zip_code || '',
        atc_2306: supplier.atc_2306 || '',
        nature_of_payment_2306: supplier.nature_of_payment_2306 || '',
        atc_2307: supplier.atc_2307 || '',
        nature_of_payment_2307: supplier.nature_of_payment_2307 || '',
        tax_rate_2306: supplier.tax_rate_2306?.toString() || '0',
        tax_rate_2307: supplier.tax_rate_2307?.toString() || '0'
    });
    setIsAddSupplierOpen(true);
  };

  const handleSaveSupplier = async () => {
    try {
        if (!newSupplierData.name) {
            showSnackbar('Supplier Name is required', 'warning');
            return;
        }

        const payload = {
            ...newSupplierData,
            tax_rate_2306: parseFloat(newSupplierData.tax_rate_2306 || 0),
            tax_rate_2307: parseFloat(newSupplierData.tax_rate_2307 || 0)
        };

        let error;
        
        if (isEditMode && editingSupplierId) {
            // Update existing supplier
            const { error: updateError } = await supabase
                .from('suppliers')
                .update(payload)
                .eq('id', editingSupplierId);
            error = updateError;
        } else {
            // Insert new supplier
            const { error: insertError } = await supabase
                .from('suppliers')
                .insert([payload]);
            error = insertError;
        }

        if (error) throw error;

        // Refresh similar suppliers to show the new one
        fetchSimilarSuppliers(selectedDocument.name);
        setIsAddSupplierOpen(false);
        showSnackbar(isEditMode ? 'Supplier updated successfully!' : 'Supplier added successfully!', 'success');

    } catch (error) {
        console.error('Error saving supplier:', error);
        showSnackbar('Error saving supplier: ' + error.message, 'error');
    }
  };

  const handleOpenSelectSupplier = () => {
    const initialQuery = selectedDocument?.name || '';
    setManualSupplierSearch(initialQuery);
    setIsSelectSupplierOpen(true);
    if (initialQuery) {
      handleSearchManualSuppliers(initialQuery);
    } else {
      setManualSupplierResults([]);
    }
  };

  const handleSearchManualSuppliers = async (query) => {
    const q = (query ?? manualSupplierSearch).trim();
    setManualSupplierLoading(true);
    try {
      let request = supabase
        .from('suppliers')
        .select('*')
        .order('name', { ascending: true })
        .limit(50);

      if (q) {
        request = request.ilike('name', `%${q}%`);
      }

      const { data, error } = await request;
      if (error) throw error;
      setManualSupplierResults(data || []);
    } catch (error) {
      console.error('Error searching suppliers manually:', error);
      showSnackbar('Error searching suppliers: ' + error.message, 'error');
    } finally {
      setManualSupplierLoading(false);
    }
  };

  const handleUseManualSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setIsSelectSupplierOpen(false);
  };

  const handlePrintBirForm = async (formType) => {
    try {
        // 1. Get Supplier Details
        const supplierDetails = activeSupplier || {};

        // 2. Payor Details (Retrieve from Supabase, fallback to localStorage or default)
        const getPayorDetails = async () => {
            let settings = null;
            
            // Try Supabase first
            try {
                const { data, error } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'logbook_settings')
                    .maybeSingle();
                
                if (data && data.value) {
                    settings = data.value;
                }
            } catch (err) {
                console.warn('Failed to fetch payor settings from Supabase, falling back to local storage', err);
            }

            // Fallback to localStorage
            if (!settings) {
                try {
                    const storedSettings = localStorage.getItem('logbook_settings');
                    if (storedSettings) {
                        settings = JSON.parse(storedSettings);
                    }
                } catch (e) {
                    console.error('Error reading payor settings from local storage:', e);
                }
            }

            if (settings && settings.payor) {
                return {
                    name: settings.payor.name || '',
                    tin: settings.payor.tin || '',
                    address: settings.payor.address || '',
                    zip: settings.payor.zip || ''
                };
            }

            // Default fallback
            return {
                name: 'PROVINCIAL GOVERNMENT OF SURIGAO DEL SUR',
                tin: '000-000-000-000',
                address: 'Capitol Hills, Telaje, Tandag City',
                zip: '8300'
            };
        };

        const payorDetails = await getPayorDetails();

        // 3. Map Payments
        const payments = selectedDocument.vouchers ? selectedDocument.vouchers.flatMap(v => {
            // Determine rates based on supplier data or defaults
            const rate = formType === '2306' 
                ? (supplierDetails.tax_rate_2306 || 0) 
                : (supplierDetails.tax_rate_2307 || 0);
            
            const amount = parseFloat(v.amount || 0);
            const ratePct = parseFloat(rate) || 0;
            const { tax: taxWithheld } = calculateBirTax(amount, ratePct);

            // Determine description (use remarks or description or fallback)
            let description = v.remarks || v.description || 'Professional Services'; // Default nature
            const supplierNature = formType === '2306' ? supplierDetails.nature_of_payment_2306 : supplierDetails.nature_of_payment_2307;
            if (supplierNature) {
                description = supplierNature;
            }

            return {
                description: description,
                atc: (formType === '2306' ? supplierDetails.atc_2306 : supplierDetails.atc_2307) || '',
                amount: amount,
                taxWithheld: taxWithheld
            };
        }) : [];

        // 3a. Tax Calculation Summary for Mapping
        let taxBase = selectedDocument.amount || 0;
        if (typeof taxBase === 'string') {
            taxBase = parseFloat(taxBase.replace(/[^0-9.-]/g, '')) || 0;
        }
        const summaryRate2306 = supplierDetails.tax_rate_2306 || 0;
        const summaryRate2307 = supplierDetails.tax_rate_2307 || 0;
        const { tax: summaryTax2306 } = calculateBirTax(taxBase, summaryRate2306);
        const { tax: summaryTax2307 } = calculateBirTax(taxBase, summaryRate2307);
        const summaryTotalTax = summaryTax2306 + summaryTax2307;
        const summaryNetAmount = taxBase - summaryTotalTax;

        // 4. Construct Data Object
        const formData = {
            payee: {
                name: selectedDocument.name,
                tin: supplierDetails.tin || '',
                address: supplierDetails.address || '',
                zip: supplierDetails.zip_code || ''
            },
            payor: payorDetails,
            dateRange: {
                startDate: selectedDocument.check_date || new Date().toISOString(), 
                endDate: selectedDocument.check_date || new Date().toISOString()
            },
            payments: payments,
            // Additional fields for dynamic mapping
            transaction: {
                cafoa_no: selectedDocument.cafoa_number || '',
                dv_no: selectedDocument.vouchers && selectedDocument.vouchers.length > 0 ? selectedDocument.vouchers[0].dv_number : '',
                voucher_amount: selectedDocument.vouchers && selectedDocument.vouchers.length > 0 ? formatCurrency(selectedDocument.vouchers[0].amount) : '',
                check_no: selectedDocument.vouchers && selectedDocument.vouchers[0].checks && selectedDocument.vouchers[0].checks.length > 0 ? selectedDocument.vouchers[0].checks[0].check_number : '',
                check_date: selectedDocument.vouchers && selectedDocument.vouchers.length > 0 && selectedDocument.vouchers[0].checks && selectedDocument.vouchers[0].checks.length > 0 ? selectedDocument.vouchers[0].checks[0].check_date : (selectedDocument.check_date || ''),
                voucher_date: selectedDocument.vouchers && selectedDocument.vouchers.length > 0 ? selectedDocument.vouchers[0].voucher_date : (selectedDocument.voucher_created_date || ''),
                particulars: selectedDocument.remarks || ''
            },
            supplier: {
                atc: (formType === '2306' ? supplierDetails.atc_2306 : supplierDetails.atc_2307) || '',
                nature_of_payment: (formType === '2306' ? supplierDetails.nature_of_payment_2306 : supplierDetails.nature_of_payment_2307) || '',
                atc_2306: supplierDetails.atc_2306 || '',
                nature_of_payment_2306: supplierDetails.nature_of_payment_2306 || '',
                atc_2307: supplierDetails.atc_2307 || '',
                nature_of_payment_2307: supplierDetails.nature_of_payment_2307 || '',
                tax_rate_2306: supplierDetails.tax_rate_2306 || '0',
                tax_rate_2307: supplierDetails.tax_rate_2307 || '0'
            }
        };

        // 5. Fetch Template Configuration
        let templateConfig = null;
        let templateUrl = null;
        
        try {
            const { data: templateData, error: templateError } = await supabase
                .from('bir_form_templates')
                .select('*')
                .eq('form_type', formType)
                .maybeSingle(); // Use maybeSingle to avoid error on no rows
            
            if (!templateError && templateData) {
                templateConfig = templateData.mappings;
                templateUrl = templateData.file_url;
                console.log('Using custom template:', templateData);
            }
        } catch (err) {
            console.warn('Could not fetch custom template, using default.', err);
        }

        // Helper to strip currency symbol for PDF (standard fonts don't support ₱)
        const cleanCurrency = (val) => {
            if (!val) return '';
            return val.replace(/[₱\u20B1]/g, '').trim();
        };

        // Update formData to use clean currency for mapped fields
        formData.transaction.voucher_amount = cleanCurrency(formData.transaction.voucher_amount);
        formData.payments = formData.payments.map(p => ({
            ...p,
            amount: p.amount, // Keep number for calculations
            // We don't use the formatted string here, calculations are done in filler
        }));

        const formatForPdf = (val) => cleanCurrency(formatCurrency(val));

        formData.transaction.cafoa_amount = formatForPdf(taxBase);
        formData.tax = {
            cafoa_amount: formatForPdf(taxBase),
            rate_2306: `${summaryRate2306 || 0}%`,
            tax_2306: formatForPdf(summaryTax2306),
            rate_2307: `${summaryRate2307 || 0}%`,
            tax_2307: formatForPdf(summaryTax2307),
            total_tax: formatForPdf(summaryTotalTax),
            net_amount: formatForPdf(summaryNetAmount)
        };

        // 6. Generate PDF
        console.log('Generating PDF with Form Data:', formData);
        console.log('Template Config:', templateConfig);
        
        const pdfBytes = await fillBirForm(formType, formData, templateConfig, templateUrl);
        
        // 7. Create Blob URL for Preview
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        setPreviewPdfUrl(url);
        setPreviewTitle(`Preview BIR Form ${formType} - ${selectedDocument.name}`);
        setIsPreviewOpen(true);

    } catch (error) {
        console.error(`Error generating BIR Form ${formType}:`, error);
        showSnackbar(`Failed to generate BIR Form ${formType}: ${error.message}`, 'error');
    }
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
        setPreviewPdfUrl(null);
    }
  };
  
  // Sorting State
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('created_at');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  const handleOpenMapper = () => {
    setIsMapperOpen(true);
  };

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
        setSearchResults(null);
        return;
    }

    setLoading(true);
    setSearchResults(null);
    try {
      // Sanitize query to prevent PostgREST syntax errors
      const sanitizedQuery = query.replace(/[(),]/g, '').trim();
      if (!sanitizedQuery) {
        setLoading(false);
        return;
      }

      // 1. Search elogbook_transactions by Payee, CAFOA, Voucher, Check
      const { data: transactions, error } = await supabase
        .from('elogbook_transactions')
        .select('*')
        .or(`payee_name.ilike.%${sanitizedQuery}%,voucher_payee.ilike.%${sanitizedQuery}%,cafoa_number.ilike.%${sanitizedQuery}%,dv_number.ilike.%${sanitizedQuery}%,check_number.ilike.%${sanitizedQuery}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!transactions || transactions.length === 0) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      // 2. Map flat transactions to expected structure
      const enrichedResults = transactions.map(t => {
        // Construct vouchers array
        const vouchers = [];
        if (t.dv_number) {
            const checks = [];
            if (t.check_number) {
                checks.push({
                    check_number: t.check_number,
                    check_date: t.check_date,
                    amount: t.check_amount,
                    remarks: t.check_remarks
                });
            }
            
            vouchers.push({
                dv_number: t.dv_number,
                amount: t.voucher_amount,
                description: t.voucher_particulars,
                remarks: t.remarks,
                voucher_date: t.voucher_created_date,
                checks: checks
            });
        }

        return {
            id: t.id,
            name: t.payee_name || t.voucher_payee, // Map payee back to name for compatibility, fallback to voucher payee
            cafoa_number: t.cafoa_number,
            amount: t.cafoa_amount, // CAFOA amount
            remarks: t.voucher_particulars || t.remarks, // Prefer particulars as it holds the description
            created_at: t.created_at,
            check_date: t.check_date,
            check_number: t.check_number,
            voucher_created_date: t.voucher_created_date,
            vouchers: vouchers
        };
      });

      setSearchResults(enrichedResults);

    } catch (error) {
      console.error('Search Error:', error);
      showSnackbar('Search failed: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce Effect
  useEffect(() => {
    const timer = setTimeout(() => {
        if (searchQuery) {
            performSearch(searchQuery);
        } else {
            setSearchResults(null);
        }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(dateString).toLocaleDateString();
  };

  const flattenedData = useMemo(() => {
    if (!searchResults || !Array.isArray(searchResults)) return [];
    
    return searchResults.map(cafoa => {
             const payeeName = cafoa.name;
             const dvNumbers = cafoa.vouchers?.map(v => v.dv_number).join(', ') || '';
             const checkNumbers = cafoa.vouchers?.flatMap(v => v.checks || []).map(c => c.check_number).join(', ') || '';
             // Sort check dates to get the earliest one for sorting comparison
             const checkDates = cafoa.vouchers?.flatMap(v => v.checks || []).map(c => c.check_date).sort()[0] || ''; 
             const voucherAmounts = cafoa.vouchers?.map(v => formatCurrency(v.amount)).join(', ') || '';
             const voucherDates = cafoa.vouchers?.map(v => formatDate(v.voucher_date)).join(', ') || '';
             const totalVoucherAmount = cafoa.vouchers?.reduce((sum, v) => sum + (Number(v.amount) || 0), 0) || 0;
             
             // Get particulars/description from CAFOA or fallback to Voucher
             const voucherDescriptions = cafoa.vouchers?.map(v => v.description).filter(Boolean).join(', ');
             const voucherRemarks = cafoa.vouchers?.map(v => v.remarks).filter(Boolean).join(', ');
             const displayRemarks = cafoa.remarks || voucherDescriptions || voucherRemarks || '';

             return {
                 ...cafoa,
                 remarks: displayRemarks,
                 payeeName, 
                 dvNumbers,
                 checkNumbers,
                 checkDates, 
                 checkDatesDisplay: cafoa.vouchers?.flatMap(v => v.checks || []).map(c => formatDate(c.check_date)).join(', ') || 'N/A',
                 voucherAmounts,
                 voucherDates,
                 totalVoucherAmount
             };
        });
  }, [searchResults]);

  const sortedData = useMemo(() => {
      return [...flattenedData].sort((a, b) => {
          let valueA = '';
          let valueB = '';

          switch (orderBy) {
              case 'payeeName': valueA = a.payeeName?.toLowerCase() || ''; valueB = b.payeeName?.toLowerCase() || ''; break;
              case 'cafoa_number': valueA = a.cafoa_number?.toLowerCase() || ''; valueB = b.cafoa_number?.toLowerCase() || ''; break;
              case 'cafoa_amount': valueA = Number(a.amount) || 0; valueB = Number(b.amount) || 0; break;
              case 'dv_number': valueA = a.dvNumbers?.toLowerCase() || ''; valueB = b.dvNumbers?.toLowerCase() || ''; break;
              case 'voucher_amount': valueA = a.totalVoucherAmount; valueB = b.totalVoucherAmount; break;
              case 'voucher_date': valueA = a.voucherDates || ''; valueB = b.voucherDates || ''; break;
              case 'check_number': valueA = a.checkNumbers?.toLowerCase() || ''; valueB = b.checkNumbers?.toLowerCase() || ''; break;
              case 'check_date': valueA = a.checkDates || ''; valueB = b.checkDates || ''; break;
              case 'remarks': valueA = a.remarks?.toLowerCase() || ''; valueB = b.remarks?.toLowerCase() || ''; break;
              default: return 0;
          }

          if (valueA < valueB) return order === 'asc' ? -1 : 1;
          if (valueA > valueB) return order === 'asc' ? 1 : -1;
          return 0;
      });
  }, [flattenedData, order, orderBy]);

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 8 }}>
      {/* Search Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
          border: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
                <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ letterSpacing: '-0.5px' }}>
                    BIR Forms Generation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Search documents, manage suppliers, and generate 2306/2307 forms.
                </Typography>
            </Box>
            <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={handleOpenMapper}
                sx={{ 
                    px: 3, 
                    py: 1, 
                    textTransform: 'none', 
                    borderRadius: 3, 
                    borderColor: 'rgba(0,0,0,0.12)', 
                    color: 'text.primary', 
                    fontWeight: 600,
                    bgcolor: 'white',
                    '&:hover': { borderColor: 'rgba(0,0,0,0.24)', bgcolor: '#f5f5f5' } 
                }}
            >
                Template Mapper
            </Button>
        </Box>

        <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Payee Name, CAFOA No., or DV No..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 28, mr: 1 }} />
                    </InputAdornment>
                ),
                sx: { 
                    borderRadius: 3, 
                    bgcolor: 'white',
                    height: 56,
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 2 }
                }
            }}
        />
      </Paper>

      {/* Results Section */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={40} thickness={4} sx={{ color: 'text.secondary' }} />
        </Box>
      )}

      {!loading && !selectedDocument && !searchResults && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 6, 
            textAlign: 'center', 
            color: 'text.secondary', 
            border: '1px dashed #e0e0e0', 
            borderRadius: 3,
            bgcolor: '#fafafa'
          }}
        >
            <DescriptionIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
                Ready to generate BIR Forms?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                Start by searching for a Payee Name in the search bar above. 
                We'll find the relevant CAFOA, Vouchers, and Checks for you.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip label="Search by Payee Name" icon={<SearchIcon />} />
                <Chip label="Select Document" icon={<ReceiptIcon />} />
                <Chip label="Generate 2306 / 2307" icon={<DescriptionIcon />} />
            </Box>
        </Paper>
      )}

      {!loading && !selectedDocument && searchResults && searchResults.length === 0 && (
        <Box sx={{ p: 8, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">No documents found.</Typography>
        </Box>
      )}

      {!loading && !selectedDocument && sortedData.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
            <Table size="medium">
                <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                        {[
                            { id: 'payeeName', label: 'Payee name', width: '20%' },
                            { id: 'cafoa_number', label: 'cafoa no.', width: '12%' },
                            { id: 'cafoa_amount', label: 'cafoa amount', width: '12%' },
                            { id: 'dv_number', label: 'dv no.', width: '10%' },
                            { id: 'voucher_amount', label: 'voucher amount', width: '10%' },
                            { id: 'voucher_date', label: 'voucher date', width: '10%' },
                            { id: 'remarks', label: 'particulars', width: '16%' }
                        ].map((headCell) => (
                            <TableCell 
                                key={headCell.id}
                                sx={{ 
                                    fontWeight: '700', 
                                    color: 'text.secondary', 
                                    py: 2.5,
                                    borderBottom: '1px solid #e0e0e0',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    width: headCell.width
                                }}
                                sortDirection={orderBy === headCell.id ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : 'asc'}
                                    onClick={createSortHandler(headCell.id)}
                                >
                                    {headCell.label}
                                </TableSortLabel>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedData.map((row) => (
                    <TableRow 
                        key={row.id} 
                        hover 
                        onClick={() => setSelectedDocument(row)}
                        sx={{ 
                            '&:hover': { bgcolor: '#f0f4f8' }, 
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        <TableCell sx={{ fontWeight: '600', color: 'text.primary', fontSize: '0.95rem' }}>{row.payeeName}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace', letterSpacing: '-0.5px' }}>{row.cafoa_number}</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: 'primary.main' }}>
                            {formatCurrency(row.amount)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                            {row.dvNumbers || '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                            {row.voucherAmounts || '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {row.voucherDates || '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'text.secondary', fontSize: '0.85rem' }}>
                            {row.remarks || '-'}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>
      )}

      {/* Detail View (Replaces Results) */}
      {selectedDocument && (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button 
                    variant="text" 
                    onClick={() => setSelectedDocument(null)}
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                        color: 'text.secondary', 
                        textTransform: 'none', 
                        fontWeight: 600,
                        '&:hover': { color: 'text.primary', bgcolor: 'transparent', textDecoration: 'underline' } 
                    }}
                >
                    Back to Search
                </Button>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => handlePrintBirForm('2306')}
                        startIcon={<DescriptionIcon />}
                        sx={{ 
                            textTransform: 'none', 
                            color: 'primary.main', 
                            borderColor: 'primary.main',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            '&:hover': { borderColor: 'primary.dark', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                        }}
                    >
                        Print 2306
                    </Button>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={() => handlePrintBirForm('2307')}
                        startIcon={<DescriptionIcon />}
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            boxShadow: 'none',
                            '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                        }}
                    >
                        Print 2307
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden', mb: 4 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>Payee Name</TableCell>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>CAFOA No.</TableCell>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>DV No.</TableCell>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>Voucher Amt</TableCell>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>Voucher Date</TableCell>
                                <TableCell sx={{ fontWeight: '700', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', py: 2.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>Particulars</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedDocument.vouchers && selectedDocument.vouchers.length > 0 ? (
                                selectedDocument.vouchers.map((voucher, vIdx) => {
                                    // Determine voucher particulars
                                    const voucherParticulars = voucher.description || voucher.remarks || '';
                                    
                                    return (
                                        <TableRow key={voucher.id} hover>
                                            <TableCell sx={{ color: 'text.primary', verticalAlign: 'top' }}>
                                                {vIdx === 0 ? selectedDocument.name : ''}
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>
                                                {vIdx === 0 ? selectedDocument.cafoa_number : ''}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: '600', color: 'text.primary', verticalAlign: 'top' }}>
                                                {vIdx === 0 ? formatCurrency(selectedDocument.amount) : ''}
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>{voucher.dv_number}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>{formatCurrency(voucher.amount)}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>{formatDate(voucher.voucher_date)}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', maxWidth: 200, whiteSpace: 'normal', verticalAlign: 'top' }}>
                                                {voucherParticulars || selectedDocument.remarks || '-'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                // No vouchers linked
                                <TableRow hover>
                                    <TableCell sx={{ color: 'text.primary' }}>{selectedDocument.name}</TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>{selectedDocument.cafoa_number}</TableCell>
                                    <TableCell sx={{ fontWeight: '600', color: 'text.primary' }}>{formatCurrency(selectedDocument.amount)}</TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>-</TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>-</TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>-</TableCell>
                                    <TableCell sx={{ color: 'text.secondary', maxWidth: 200, whiteSpace: 'normal' }}>
                                        {selectedDocument.remarks || '-'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Similar Suppliers Section */}
            {activeSupplier && (
                <Box sx={{ mt: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="700" color="text.primary">
                            Suggested Supplier Details
                        </Typography>
                        <Chip 
                            label="Auto-detected" 
                            size="small" 
                            color="success" 
                            variant="outlined" 
                            sx={{ ml: 2, height: 24, fontSize: '0.75rem', fontWeight: 600 }} 
                        />
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleOpenSelectSupplier}
                            sx={{ ml: 2, textTransform: 'none' }}
                        >
                            Select supplier manually
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        We found supplier details related to "{selectedDocument.name}". Verify or override below.
                    </Typography>

                    <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>TIN</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>Address</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>Zip Code</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>ATC 2306</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>ATC 2307</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>Tax 2306 (%)</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>Tax 2307 (%)</TableCell>
                                        <TableCell sx={{ fontWeight: '700', color: 'text.secondary', fontSize: '0.75rem', py: 2, width: 50 }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow key={activeSupplier.id} hover>
                                        <TableCell sx={{ fontWeight: '600', color: 'text.primary' }}>{activeSupplier.name}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{activeSupplier.tin || '-'}</TableCell>
                                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeSupplier.address || '-'}</TableCell>
                                        <TableCell>{activeSupplier.zip_code || '-'}</TableCell>
                                        <TableCell>{activeSupplier.atc_2306 || '-'}</TableCell>
                                        <TableCell>{activeSupplier.atc_2307 || '-'}</TableCell>
                                        <TableCell>{activeSupplier.tax_rate_2306 || '0'}%</TableCell>
                                        <TableCell>{activeSupplier.tax_rate_2307 || '0'}%</TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                onClick={() => handleOpenEditSupplier(activeSupplier)}
                                                sx={{ minWidth: 32, height: 32, borderRadius: '50%', p: 0, color: 'text.secondary' }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Tax Calculation Section */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight="700" gutterBottom>
                            Tax Calculation Summary
                        </Typography>
                        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table size="medium">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '20%' }}>CAFOA Amount</TableCell>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '10%' }}>Tax 2306 (%)</TableCell>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '15%' }}>2306 Tax</TableCell>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '10%' }}>Tax 2307 (%)</TableCell>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '15%' }}>2307 Tax</TableCell>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '15%', bgcolor: '#eef2ff' }}>Total Tax</TableCell>
                                            <TableCell sx={{ fontWeight: '700', color: 'text.secondary', width: '15%', bgcolor: '#e8f5e9' }}>Net Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            let taxBase = selectedDocument.amount || 0;
                                            if (typeof taxBase === 'string') {
                                                taxBase = parseFloat(taxBase.replace(/[^0-9.-]/g, '')) || 0;
                                            }

                                            const rate2306 = activeSupplier?.tax_rate_2306 || 0;
                                            const rate2307 = activeSupplier?.tax_rate_2307 || 0;
                                            
                                            const { tax: tax2306 } = calculateBirTax(taxBase, rate2306);
                                            const { tax: tax2307 } = calculateBirTax(taxBase, rate2307);
                                            
                                            const totalTax = tax2306 + tax2307;
                                            const netAmount = taxBase - totalTax;
                                            
                                            return (
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: '700', color: 'text.primary', fontSize: '1.1rem' }}>{formatCurrency(taxBase)}</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{rate2306}%</TableCell>
                                                    <TableCell sx={{ fontWeight: '600', color: 'text.primary' }}>{formatCurrency(tax2306)}</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{rate2307}%</TableCell>
                                                    <TableCell sx={{ fontWeight: '600', color: 'text.primary' }}>{formatCurrency(tax2307)}</TableCell>
                                                    <TableCell sx={{ fontWeight: '700', color: 'primary.main', fontSize: '1.1rem', bgcolor: '#eef2ff' }}>{formatCurrency(totalTax)}</TableCell>
                                                    <TableCell sx={{ fontWeight: '700', color: 'success.main', fontSize: '1.1rem', bgcolor: '#e8f5e9' }}>{formatCurrency(netAmount)}</TableCell>
                                                </TableRow>
                                            );
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                </Box>
            )}

            {/* No Similar Suppliers Found - Option to Add */}
            {searchPerformed && !supplierLoading && similarSuppliers.length === 0 && (
                <Box sx={{ mt: 4, p: 3, border: '1px dashed #e0e0e0', borderRadius: 2, textAlign: 'center', bgcolor: '#f9fafb' }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        No suppliers found with a similar name.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This payee might not be in the supplier database yet.
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddSupplier}
                        sx={{ textTransform: 'none' }}
                    >
                        Add "{selectedDocument.name}" as Supplier
                    </Button>
                </Box>
            )}

            {/* Add/Edit Supplier Dialog */}
            <Dialog open={isAddSupplierOpen} onClose={() => setIsAddSupplierOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditMode ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Supplier Name"
                            fullWidth
                            value={newSupplierData.name}
                            onChange={(e) => setNewSupplierData({...newSupplierData, name: e.target.value})}
                        />
                        <TextField
                            label="TIN"
                            fullWidth
                            value={newSupplierData.tin}
                            onChange={(e) => setNewSupplierData({...newSupplierData, tin: e.target.value})}
                        />
                        <TextField
                            label="Address"
                            fullWidth
                            multiline
                            rows={2}
                            value={newSupplierData.address}
                            onChange={(e) => setNewSupplierData({...newSupplierData, address: e.target.value})}
                        />
                        <TextField
                            label="Zip Code"
                            fullWidth
                            value={newSupplierData.zip_code}
                            onChange={(e) => setNewSupplierData({...newSupplierData, zip_code: e.target.value})}
                        />
                        
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                            BIR Form 2306
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="ATC (2306)"
                                fullWidth
                                value={newSupplierData.atc_2306}
                                onChange={(e) => setNewSupplierData({...newSupplierData, atc_2306: e.target.value})}
                            />
                            <TextField
                                label="Tax Rate (%)"
                                fullWidth
                                type="number"
                                value={newSupplierData.tax_rate_2306}
                                onChange={(e) => setNewSupplierData({...newSupplierData, tax_rate_2306: e.target.value})}
                            />
                        </Box>
                        <TextField
                            label="Nature of Payment (2306)"
                            fullWidth
                            value={newSupplierData.nature_of_payment_2306}
                            onChange={(e) => setNewSupplierData({...newSupplierData, nature_of_payment_2306: e.target.value})}
                        />

                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                            BIR Form 2307
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="ATC (2307)"
                                fullWidth
                                value={newSupplierData.atc_2307}
                                onChange={(e) => setNewSupplierData({...newSupplierData, atc_2307: e.target.value})}
                            />
                            <TextField
                                label="Tax Rate (%)"
                                fullWidth
                                type="number"
                                value={newSupplierData.tax_rate_2307}
                                onChange={(e) => setNewSupplierData({...newSupplierData, tax_rate_2307: e.target.value})}
                            />
                        </Box>
                        <TextField
                            label="Nature of Payment (2307)"
                            fullWidth
                            value={newSupplierData.nature_of_payment_2307}
                            onChange={(e) => setNewSupplierData({...newSupplierData, nature_of_payment_2307: e.target.value})}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsAddSupplierOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveSupplier} variant="contained">
                        {isEditMode ? 'Update Supplier' : 'Save Supplier'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isSelectSupplierOpen} onClose={() => setIsSelectSupplierOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Select Supplier Manually</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
                        <TextField
                            label="Search Supplier by Name"
                            fullWidth
                            value={manualSupplierSearch}
                            onChange={(e) => setManualSupplierSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchManualSuppliers();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => handleSearchManualSuppliers()}
                            disabled={manualSupplierLoading}
                        >
                            Search
                        </Button>
                    </Box>
                    {manualSupplierLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>TIN</TableCell>
                                        <TableCell>Address</TableCell>
                                        <TableCell width={80}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {manualSupplierResults.map((supplier) => (
                                        <TableRow key={supplier.id} hover>
                                            <TableCell>{supplier.name}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{supplier.tin || '-'}</TableCell>
                                            <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {supplier.address || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleUseManualSupplier(supplier)}
                                                >
                                                    Use
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {manualSupplierResults.length === 0 && !manualSupplierLoading && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                {manualSupplierSearch.trim()
                                                  ? 'No suppliers found. Adjust your search.'
                                                  : 'Type a name and click Search to find suppliers.'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsSelectSupplierOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
      )}

      {/* Template Mapper Dialog */}
      <TemplateMapper
        open={isMapperOpen}
        onClose={() => setIsMapperOpen(false)}
        availableFields={[
            // Payee Details
            { key: 'payee.tin', label: 'Payee: TIN' },
            { key: 'payee.name', label: 'Payee: Name' },
            { key: 'payee.address', label: 'Payee: Address' },
            { key: 'payee.zip', label: 'Payee: Zip' },

            // Payor Details
            { key: 'payor.tin', label: 'Payor: TIN' },
            { key: 'payor.name', label: 'Payor: Name' },
            { key: 'payor.address', label: 'Payor: Address' },
            { key: 'payor.zip', label: 'Payor: Zip' },

            // Transaction Details
            { key: 'transaction.cafoa_no', label: 'Transaction: CAFOA No.' },
            { key: 'transaction.cafoa_amount', label: 'Transaction: CAFOA Amount' },
            { key: 'transaction.dv_no', label: 'Transaction: DV No.' },
            { key: 'transaction.voucher_amount', label: 'Transaction: Voucher Amount' },
            { key: 'transaction.check_no', label: 'Transaction: Check No.' },
            { key: 'transaction.check_date', label: 'Transaction: Check Date' },
            { key: 'transaction.particulars', label: 'Transaction: Particulars' },
            { key: 'dateRange.startDate', label: 'Transaction: Period From' },
            { key: 'dateRange.endDate', label: 'Transaction: Period To' },
            { key: 'dateRange.from', label: 'Transaction: FROM (Date)' },
            { key: 'dateRange.to', label: 'Transaction: TO (Date)' },
            { key: 'totals.amount', label: 'Transaction: Total Amount' },
            { key: 'totals.tax', label: 'Transaction: Total Tax Withheld' },

            // Supplier Details (From Database)
            { key: 'supplier.atc', label: 'Supplier: ATC (Current Form)' },
            { key: 'supplier.nature_of_payment', label: 'Supplier: Nature of Payment (Current Form)' },
            { key: 'supplier.atc_2306', label: 'Supplier: ATC (2306)' },
            { key: 'supplier.nature_of_payment_2306', label: 'Supplier: Nature of Payment (2306)' },
            { key: 'supplier.atc_2307', label: 'Supplier: ATC (2307)' },
            { key: 'supplier.nature_of_payment_2307', label: 'Supplier: Nature of Payment (2307)' },
            { key: 'supplier.tax_rate_2306', label: 'Supplier: Tax Rate 2306' },
            { key: 'supplier.tax_rate_2307', label: 'Supplier: Tax Rate 2307' },

            // Tax Calculation Summary
            { key: 'tax.cafoa_amount', label: 'Tax Summary: CAFOA Amount' },
            { key: 'tax.rate_2306', label: 'Tax Summary: Tax Rate 2306 (%)' },
            { key: 'tax.tax_2306', label: 'Tax Summary: 2306 Tax' },
            { key: 'tax.rate_2307', label: 'Tax Summary: Tax Rate 2307 (%)' },
            { key: 'tax.tax_2307', label: 'Tax Summary: 2307 Tax' },
            { key: 'tax.total_tax', label: 'Tax Summary: Total Tax' },
            { key: 'tax.net_amount', label: 'Tax Summary: Net Amount' },

            // Table Columns (Dynamic Rows)
            { key: 'table.nature', label: 'Table Column: Nature of Payment' },
            { key: 'table.atc', label: 'Table Column: ATC' },
            { key: 'table.amount', label: 'Table Column: Amount' },
            { key: 'table.tax', label: 'Table Column: Tax Withheld' }
        ]}
      />

      {/* PDF Preview Dialog */}
      <Dialog open={isPreviewOpen} onClose={handleClosePreview} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {previewTitle}
            <Button onClick={handleClosePreview} color="inherit">Close</Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100%' }}>
            {previewPdfUrl && (
                <iframe
                    src={previewPdfUrl}
                    title="PDF Preview"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                />
            )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
            variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BIRForms;
